import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { RootReducerStateType } from '../../reducers';
import { AuthUserState } from '../../reducers/authUser';
import { getAuthUser } from '../../common/auth';
import { postFetcher, getFetcher } from '../../common/fetcher';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

const OPERATIONS_ASSISTANT_ASK_URL = '/ai_analytics/operations_assistant/ask';
const OPERATIONS_UNMATCHED_URL = '/ai_analytics/operations_tracking/unmatched_emails';

interface AssistantSearch {
    tool: string;
    query: string;
    lot_num?: string | null;
}

interface AssistantAskResponse {
    question?: string;
    answer?: string;
    searches?: AssistantSearch[];
    error?: string;
}

interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
    searches?: AssistantSearch[];
}

interface UnmatchedEmail {
    id: string;
    subject?: string | null;
    labels?: string[];
}

interface UnmatchedEmailsResponse {
    today?: string | null;
    unmatched_emails?: UnmatchedEmail[];
}

const GMAIL_MESSAGE_BASE = 'https://mail.google.com/mail/u/0/#all/';
const EMAIL_SCHEME = 'email:';

// Feather "bell" icon inline so we don't pull in @mui/icons-material.
const BellIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

// Keep our custom email: scheme intact (react-markdown's default transformer
// rewrites unknown schemes to javascript:void(0)); delegate the rest to it.
const transformLinkUri = (uri: string): string => (uri && uri.startsWith(EMAIL_SCHEME) ? uri : (ReactMarkdown as any).uriTransformer(uri));

// email:<id> citations become Gmail deep links opened in a new tab; other links
// render normally.
const markdownRenderers = {
    link: ({ href, children }: any) => {
        const isEmail = typeof href === 'string' && href.startsWith(EMAIL_SCHEME);
        const url = isEmail ? `${GMAIL_MESSAGE_BASE}${encodeURIComponent(href.slice(EMAIL_SCHEME.length))}` : href;
        return (
            <a href={url} target="_blank" rel="noopener noreferrer">
                {children}
            </a>
        );
    },
};

// Full Markdown (incl. GFM tables, task lists, strikethrough) via react-markdown.
// react-markdown v5 uses `plugins`/`renderers`; element styling is done with MUI
// sx selectors on the wrapper so we don't depend on the v6+ `components` API.
const MarkdownText = ({ text }: { text: string }) => (
    <Box
        sx={{
            fontSize: '0.875rem',
            lineHeight: 1.5,
            '& p': { my: 0.5 },
            '& ul, & ol': { my: 0.5, pl: 3 },
            '& li': { mb: 0.25 },
            '& a': { color: 'primary.main' },
            '& code': { bgcolor: 'grey.200', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' },
            '& pre': { bgcolor: 'grey.900', color: 'grey.100', p: 1, borderRadius: 1, overflowX: 'auto' },
            '& pre code': { bgcolor: 'transparent', p: 0, color: 'inherit' },
            '& table': { borderCollapse: 'collapse', my: 1 },
            '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
            '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 1, ml: 0, color: 'text.secondary' },
            '& h1, & h2, & h3, & h4, & h5, & h6': { my: 0.5, fontWeight: 'bold' },
            '& > :first-of-type': { mt: 0 },
            '& > :last-child': { mb: 0 },
        }}>
        <ReactMarkdown plugins={[gfm]} transformLinkUri={transformLinkUri} renderers={markdownRenderers}>
            {text}
        </ReactMarkdown>
    </Box>
);

export const OperationsTrackingIndex = () => {
    const authUserState = useSelector<RootReducerStateType, AuthUserState>((s) => s.authUser);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [question, setQuestion] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [unmatched, setUnmatched] = useState<UnmatchedEmail[]>([]);
    const [unmatchedOpen, setUnmatchedOpen] = useState<boolean>(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const authHeaders = { 'Swiss-Tech-Auth-Token': getAuthUser(authUserState).swiss_token };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    // Load the latest run's unmatched emails once on mount (best-effort).
    useEffect(() => {
        getFetcher<UnmatchedEmailsResponse>({
            url: OPERATIONS_UNMATCHED_URL,
            headers: { 'Swiss-Tech-Auth-Token': getAuthUser(authUserState).swiss_token },
            onSuccess: (res) => setUnmatched((res.unmatched_emails || []).filter((em) => em.labels && em.labels.length > 0)),
            onFail: () => undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAsk = () => {
        const trimmed = question.trim();
        if (!trimmed || loading) return;
        setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
        setQuestion('');
        setLoading(true);
        setError(null);
        postFetcher<AssistantAskResponse>({
            url: OPERATIONS_ASSISTANT_ASK_URL,
            data: { question: trimmed },
            headers: authHeaders,
            onSuccess: (res) => {
                if (res.error) {
                    setError(res.error);
                } else {
                    setMessages((prev) => [
                        ...prev,
                        { role: 'assistant', text: res.answer || 'No answer returned.', searches: res.searches },
                    ]);
                }
                setLoading(false);
            },
            onFail: (err) => {
                setError(err.parsedMsg);
                setLoading(false);
            },
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAsk();
        }
    };

    return (
        <Paper sx={{ p: 2, m: 2, display: 'flex', flexDirection: 'column', height: '60vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h6">Operations Assistant</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Ask about voyages and lots. Answers are grounded in the indexed daily summaries and source emails.
                    </Typography>
                </Box>
                <Tooltip title="Unmatched emails">
                    <IconButton onClick={() => setUnmatchedOpen(true)} sx={{ ml: 1 }}>
                        <Badge badgeContent={unmatched.length} color="error">
                            <BellIcon />
                        </Badge>
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', mt: 1, pr: 1 }}>
                {messages.length === 0 && !loading && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 2 }}>
                        No messages yet. Ask a question to get started.
                    </Typography>
                )}
                {messages.map((msg, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                        <Box
                            sx={{
                                maxWidth: '80%',
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                            }}>
                            {msg.role === 'assistant' ? (
                                <MarkdownText text={msg.text} />
                            ) : (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {msg.text}
                                </Typography>
                            )}
                            {msg.searches && msg.searches.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                    {msg.searches.map((s, sIdx) => (
                                        <Chip
                                            key={sIdx}
                                            size="small"
                                            variant="outlined"
                                            label={`${s.tool}${s.lot_num ? ` · ${s.lot_num}` : ''}: ${s.query}`}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                ))}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 1 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                <div ref={bottomRef} />
            </Box>

            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    placeholder="Ask a question…"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                />
                <Button variant="contained" onClick={handleAsk} disabled={loading || !question.trim()}>
                    Send
                </Button>
            </Box>

            <Dialog
                open={unmatchedOpen}
                onClose={() => setUnmatchedOpen(false)}
                fullWidth
                maxWidth="md"
                sx={{ marginTop: '40px', maxHeight: '90%' }}>
                <DialogTitle>Unmatched emails ({unmatched.length})</DialogTitle>
                <DialogContent dividers>
                    {unmatched.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No unmatched emails from the latest run.
                        </Typography>
                    ) : (
                        unmatched.map((em, idx) => (
                            <Box key={em.id || idx} sx={{ py: 1 }}>
                                {idx > 0 && <Divider sx={{ mb: 1 }} />}
                                <Link
                                    href={`${GMAIL_MESSAGE_BASE}${encodeURIComponent(em.id)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2">
                                    {em.subject || '(no subject)'}
                                </Link>
                                {em.labels && em.labels.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {em.labels.map((label, lIdx) => (
                                            <Chip key={lIdx} size="small" variant="outlined" label={label} />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ))
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default OperationsTrackingIndex;
